import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faCheck, faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import "../styles/single-product.css";
import NotFound from "./NotFound";

const ApiURL = process.env.REACT_APP_API_URL;

const shuffleArray = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

const SingleProduct = () => {
  const navigate = useNavigate();
  const { productSlug } = useParams();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [activeTab, setActiveTab] = useState("description");
  const [selectedIndex, setSelectedIndex] = useState(0);

  const thumbnailsRef = useRef(null);
  const thumbRefs = useRef([]);

  const questionWhatsapp = () => {
    const message = `Hola! Tengo una consulta sobre ${product.name}`;
    const phoneNumber = "5493464540919";
    window.open(
      `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`,
      "_blank",
    );
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${ApiURL}/products/${productSlug}`);
        const productData = response.data;
        setProduct(productData);
        setSelectedIndex(0);
        if (productData.relatedProducts?.length > 0) {
          setRelatedProducts(
            shuffleArray([...productData.relatedProducts]).slice(0, 4),
          );
        }
      } catch (error) {
        console.error("Error obteniendo el producto:", error);
        setNotFound(true);
      }
    };
    fetchProduct();
  }, [productSlug]);

  useEffect(() => {
    const thumb = thumbRefs.current[selectedIndex];
    const container = thumbnailsRef.current;
    if (!thumb || !container) return;
    const thumbLeft = thumb.offsetLeft;
    const thumbWidth = thumb.offsetWidth;
    const containerWidth = container.offsetWidth;
    const scrollLeft = thumbLeft - containerWidth / 2 + thumbWidth / 2;
    container.scrollTo({ left: scrollLeft, behavior: "smooth" });
  }, [selectedIndex]);

  if (notFound) return <NotFound />;

  if (!product) {
    return (
      <div className="product-loading-page">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  const hasSpecs =
    product.specifications && Object.keys(product.specifications).length > 0;
  const specEntries = hasSpecs ? Object.entries(product.specifications) : [];
  const keySpecs = specEntries.slice(0, 3);
  const allImages = [product.image, ...(product.images || [])];
  const showGallery = allImages.length > 1;

  const prevImage = () =>
    setSelectedIndex((i) => (i === 0 ? allImages.length - 1 : i - 1));
  const nextImage = () =>
    setSelectedIndex((i) => (i === allImages.length - 1 ? 0 : i + 1));

  return (
    <div className="single-product-page">
      <Helmet>
        <title>{product.name} | Cáceres Maquinarias</title>
        <meta name="description" content={product.description} />
        <meta property="og:title" content={`${product.name} | Cáceres Maquinarias`} />
        <meta property="og:description" content={product.description} />
        <meta property="og:url" content={`https://www.caceresmaquinarias.com.ar/productos/${product.slug}`} />
        <meta property="og:image" content={product.image} />
        <meta property="og:type" content="product" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={product.image} />
      </Helmet>

      <div className="sp-breadcrumbs-bar">
        <div className="sp-breadcrumbs">
          <Link to="/">Inicio</Link>
          <span className="sp-bc-sep">›</span>
          <Link to="/productos">Productos</Link>
          <span className="sp-bc-sep">›</span>
          <span className="sp-bc-current">{product.name}</span>
        </div>
      </div>

      <div className="sp-main-section">
        <div className="sp-main-container">
          <button className="sp-back-btn" onClick={() => navigate(-1)}>
            <FontAwesomeIcon icon={faArrowLeft} />
            Volver
          </button>

          <div className="sp-hero-grid">
            <div className="sp-img-col">
              <div className="sp-img-main">
                {product.brand && (
                  <div className="sp-img-badge">
                    {product.brand.name || product.brand}
                  </div>
                )}
                <img
                  src={allImages[selectedIndex]}
                  alt={product.name}
                  className="sp-product-img"
                />
                {showGallery && (
                  <>
                    <button className="sp-img-arrow sp-img-arrow-prev" onClick={prevImage}>
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <button className="sp-img-arrow sp-img-arrow-next" onClick={nextImage}>
                      <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                    <div className="sp-img-dots">
                      {allImages.map((_, i) => (
                        <span
                          key={i}
                          className={`sp-img-dot${i === selectedIndex ? " active" : ""}`}
                          onClick={() => setSelectedIndex(i)}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>

              {showGallery && (
                <div className="sp-thumbnails" ref={thumbnailsRef}>
                  {allImages.map((imgUrl, index) => (
                    <div
                      key={index}
                      ref={(el) => (thumbRefs.current[index] = el)}
                      className={`sp-thumb${selectedIndex === index ? " active" : ""}`}
                      onClick={() => setSelectedIndex(index)}
                    >
                      <img src={imgUrl} alt={`${product.name} vista ${index + 1}`} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="sp-info-col">
              {product.brand && (
                <div className="sp-brand-badge">
                  {product.brand.name || product.brand}
                </div>
              )}

              <h1 className="sp-product-title">{product.name}</h1>

              <p className="sp-short-desc">{product.description}</p>

              {keySpecs.length > 0 && (
                <div className="sp-key-specs">
                  {keySpecs.map(([label, value]) => (
                    <div key={label} className="sp-key-spec">
                      <div className="sp-key-spec-val">{value}</div>
                      <div className="sp-key-spec-label">{label}</div>
                    </div>
                  ))}
                </div>
              )}

              <div className="sp-cta-area">
                <button className="sp-btn-consult" onClick={questionWhatsapp}>
                  <FontAwesomeIcon icon={faWhatsapp} />
                  Consultar por este producto
                </button>
              </div>

              <div className="sp-disclaimer">
                <FontAwesomeIcon icon={faCheck} className="sp-disclaimer-icon" />
                <span>
                  Las imágenes son ilustrativas. Consultá stock y accesorios incluidos.
                </span>
              </div>

              <div className="sp-trust-row">
                <div className="sp-trust-item">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L3 7V12C3 16.55 6.84 20.74 12 22C17.16 20.74 21 16.55 21 12V7L12 2Z" stroke="#f37a1f" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M9 12L11 14L15 10" stroke="#f37a1f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div className="sp-trust-text">
                    <span className="sp-trust-label">Garantía oficial</span>
                    <span className="sp-trust-sub">
                      {product.brand
                        ? `Respaldo ${typeof product.brand === "object" ? product.brand.name : product.brand}`
                        : "Respaldo del fabricante"}
                    </span>
                  </div>
                </div>
                <div className="sp-trust-item">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" stroke="#f37a1f" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  <div className="sp-trust-text">
                    <span className="sp-trust-label">Repuestos originales</span>
                    <span className="sp-trust-sub">Disponibles en stock</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {(product.fullDescription || hasSpecs) && (
        <div className="sp-details-section">
          <div className="sp-details-container">
            <div className="sp-tabs-nav">
              {product.fullDescription && (
                <button
                  className={`sp-tab-btn ${activeTab === "description" ? "active" : ""}`}
                  onClick={() => setActiveTab("description")}
                >
                  Descripción
                </button>
              )}
              {hasSpecs && (
                <button
                  className={`sp-tab-btn ${activeTab === "specs" ? "active" : ""}`}
                  onClick={() => setActiveTab("specs")}
                >
                  Especificaciones técnicas
                </button>
              )}
            </div>

            <div className="sp-tab-content">
              {activeTab === "description" && product.fullDescription && (
                <div className="sp-tab-panel">
                  <div className="sp-full-desc">{product.fullDescription}</div>
                </div>
              )}
              {activeTab === "specs" && hasSpecs && (
                <div className="sp-tab-panel">
                  <table className="sp-specs-table">
                    <tbody>
                      {specEntries.map(([spec, value], index) => (
                        <tr key={spec} className={index % 2 === 0 ? "sp-even-row" : ""}>
                          <td className="sp-spec-name">{spec}</td>
                          <td className="sp-spec-value">{value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="sp-trust-band">
        <div className="sp-trust-band-container">
          <div className="sp-trust-band-item">
            <div className="sp-trust-band-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L3 7V12C3 16.55 6.84 20.74 12 22C17.16 20.74 21 16.55 21 12V7L12 2Z" fill="#f37a1f" opacity="0.15" />
                <path d="M12 2L3 7V12C3 16.55 6.84 20.74 12 22C17.16 20.74 21 16.55 21 12V7L12 2Z" stroke="#f37a1f" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9 12L11 14L15 10" stroke="#f37a1f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="sp-trust-band-text">
              <span className="sp-trust-band-title">
                Garantía oficial{" "}
                {product.brand ? typeof product.brand === "object" ? product.brand.name : product.brand : ""}
              </span>
              <span className="sp-trust-band-subtitle">Respaldo total del fabricante</span>
            </div>
          </div>

          <div className="sp-trust-band-divider" />

          <div className="sp-trust-band-item">
            <div className="sp-trust-band-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2Z" fill="#f37a1f" opacity="0.15" />
                <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2Z" stroke="#f37a1f" strokeWidth="1.5" />
                <circle cx="12" cy="9" r="2.5" stroke="#f37a1f" strokeWidth="1.5" />
              </svg>
            </div>
            <div className="sp-trust-band-text">
              <span className="sp-trust-band-title">Concesionario oficial en Casilda</span>
              <span className="sp-trust-band-subtitle">Blvd. Ovidio Lagos 2160, Casilda, Santa Fe</span>
            </div>
          </div>
        </div>
      </div>

      {relatedProducts.length > 0 && (
        <div className="sp-related-section">
          <div className="sp-related-container">
            <div className="sp-related-header">
              <div className="sp-related-label">También te puede interesar</div>
            </div>
            <div className="sp-related-grid">
              {relatedProducts.map((relatedProduct) => (
                <Link
                  key={relatedProduct.slug}
                  to={`/productos/${relatedProduct.slug}`}
                  className="sp-related-card"
                >
                  <div className="sp-related-img">
                    <img
                      src={relatedProduct.image}
                      alt={relatedProduct.name}
                      className="sp-related-img-el"
                    />
                    <div className="sp-related-overlay">
                      <span className="sp-related-btn">Ver producto</span>
                    </div>
                  </div>
                  <div className="sp-related-info">
                    {relatedProduct.brand && (
                      <p className="sp-related-brand">
                        {typeof relatedProduct.brand === "object"
                          ? relatedProduct.brand.name
                          : relatedProduct.brand}
                      </p>
                    )}
                    <h3 className="sp-related-name">{relatedProduct.name}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SingleProduct;